/** Wire-level contracts for the common macro pipeline. Policy implementations live in MacroCompiler. */
import { z } from 'zod';
import { MACRO_VISUAL_PROFILE } from '../macroProfile';
import { sha256HexSchema } from './canonicalJson';
import { sourceGraphIdentitySchema } from './sourceIdentity';

export const COMMON_MACRO_PIPELINE_VERSION = 'common-macro-pipeline-v1';
export const COMMON_MACRO_COMPILER_VERSION = '2.1.0';
export const LEGACY_COMMON_MACRO_COMPILER_VERSION = '2.0.0';
export const COMMON_MACRO_SCORING_RULE = 'raw-max-exact-inliers-10-v2';
export const HOST_VALIDATION_MAX_AGE_MS = 300_000;
// A fresh native process compiles the resident playback shaders before it can certify a package. Keep the
// nested transport deadlines ordered and inside the five-minute grant/receipt lifetime so a slow cold start
// can finish without letting a later layer time out first.
export const MACRO_NATIVE_VALIDATION_TIMEOUT_MS = 240_000;
export const MACRO_VERIFICATION_RESPONSE_TIMEOUT_MS = 245_000;
export const MACRO_VALIDATION_CLIENT_TIMEOUT_MS = 250_000;
export const COMMON_MACRO_CONTRACT_TABLE = {
  pipelineVersion: COMMON_MACRO_PIPELINE_VERSION, rawFormatVersion: 2, sourceFormatVersion: 1,
  compiledFormatVersion: 2, compilerVersion: COMMON_MACRO_COMPILER_VERSION, profile: MACRO_VISUAL_PROFILE,
  scoringRule: COMMON_MACRO_SCORING_RULE, radiusMs: 100, outlierPercent: 10, captureIntegrityVersion: 1,
  hostValidationReceiptVersion: 1, hostValidationMaxAgeMs: HOST_VALIDATION_MAX_AGE_MS,
} as const;
export const COMMON_MACRO_ERROR_CODES = ['legacy_macro_requires_rerecording', 'recording_integrity_failed',
  'host_validation_stale', 'host_validation_target_changed'] as const;

const count = z.number().int().nonnegative().safe();
const boundedId = z.string().min(1).max(128);

export const captureIntegritySchema = z.object({ version: z.literal(1), complete: z.literal(true),
  observedInputEvents: count, writtenInputEvents: count, admittedEditorPackets: count, appliedEditorPackets: count,
  submittedVideoFrames: count, encodedVideoFrames: count, indexedVideoFrames: count,
  lostInputEvents: z.literal(0), lostVideoFrames: z.literal(0), remoteSequenceGaps: z.literal(0),
}).strict().superRefine((value, context) => {
  if (value.observedInputEvents !== value.writtenInputEvents || value.admittedEditorPackets !== value.appliedEditorPackets
    || value.submittedVideoFrames !== value.encodedVideoFrames || value.encodedVideoFrames !== value.indexedVideoFrames) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'recording_integrity_failed: counters must reconcile' });
  }
});
export type CaptureIntegrity = z.infer<typeof captureIntegritySchema>;

export const hostTargetSchema = z.object({ hostId: boundedId, buildId: boundedId,
  capabilitiesDigest: sha256HexSchema }).strict();
export type HostTarget = z.infer<typeof hostTargetSchema>;

export const hostValidationReceiptSchema = z.object({ version: z.literal(1), packageSha256: sha256HexSchema,
  host: hostTargetSchema, validationContextDigest: sha256HexSchema, verifiedAtUnixMs: count, expiresAtUnixMs: count,
}).strict().superRefine((value, context) => {
  const age = value.expiresAtUnixMs - value.verifiedAtUnixMs;
  if (age <= 0 || age > HOST_VALIDATION_MAX_AGE_MS) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['expiresAtUnixMs'], message: 'Receipt lifetime must be within five minutes' });
  }
});
export type HostValidationReceipt = z.infer<typeof hostValidationReceiptSchema>;

export const compiledProvenanceSchema = z.object({ pipelineVersion: z.literal(COMMON_MACRO_PIPELINE_VERSION),
  sourceIdentity: z.lazy(() => sourceGraphIdentitySchema), rawManifestDigests: z.array(sha256HexSchema).min(1).max(64),
  calibrationPolicyDigest: sha256HexSchema, inputPolicyDigest: sha256HexSchema, evidenceDigest: sha256HexSchema,
}).strict().superRefine((value, context) => {
  if (value.rawManifestDigests.some((digest, index) => index > 0 && digest <= value.rawManifestDigests[index - 1]!)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['rawManifestDigests'], message: 'Raw manifest pins must be sorted and unique' });
  }
});
export type CompiledProvenance = z.infer<typeof compiledProvenanceSchema>;

export const commonMacroCapabilitiesSchema = z.object({ pipelineVersion: z.literal(COMMON_MACRO_PIPELINE_VERSION),
  rawFormatVersions: z.tuple([z.literal(2)]), compiledFormatVersions: z.tuple([z.literal(2)]),
  compilerVersions: z.tuple([z.literal(COMMON_MACRO_COMPILER_VERSION)]), profile: z.literal(MACRO_VISUAL_PROFILE),
  scoringRule: z.literal(COMMON_MACRO_SCORING_RULE), macroSequenceVersion: z.literal(1).optional(),
}).strict();
export type CommonMacroCapabilities = z.infer<typeof commonMacroCapabilitiesSchema>;

/** Staged capability: never infer support for the new workflow from the previous raw-v2 profile. */
export const AUTHORING_MACRO_COMPILER_VERSION = '3.1.0';
export const LEGACY_AUTHORING_MACRO_COMPILER_VERSION = '3.0.0';
export const AUTHORING_MACRO_CONTRACT_TABLE = { ...COMMON_MACRO_CONTRACT_TABLE,
  rawFormatVersion: 4, sourceFormatVersion: 2, compilerVersion: AUTHORING_MACRO_COMPILER_VERSION,
  authoringProfile: 'input-minus-100ms-v1',
} as const;
export const authoringMacroCapabilitiesSchema = commonMacroCapabilitiesSchema.extend({
  rawFormatVersions: z.union([z.tuple([z.literal(3)]), z.tuple([z.literal(4)])]), sourceFormatVersions: z.tuple([z.literal(2)]),
  compilerVersions: z.tuple([z.literal(AUTHORING_MACRO_COMPILER_VERSION)]),
  authoringProfiles: z.tuple([z.literal('input-minus-100ms-v1')]),
}).strict();
export type AuthoringMacroCapabilities = z.infer<typeof authoringMacroCapabilitiesSchema>;
